import apiClient from './api.service';

export interface CentreSession {
  open: boolean;
  remaining_ms: number;
  remaining_label: string | null;
  last_inbound_at: string | null;
}

export interface CentreOutcome {
  key: string;
  label: string;
  target_status: string | null;
  template_id: number | null;
}

export interface CentreSettings {
  store_id: number;
  queue_status_keys: string[];
  outcomes: CentreOutcome[];
  sticky_enabled?: boolean;
  sticky_hours?: number;
  away_reassign_minutes?: number;
}

export interface CentreAssignee {
  user_id: number;
  pseudo: string;
  status: string;
  open_count: number;
}

export interface CentreConversation {
  channel: 'whatsapp' | 'messenger' | 'instagram';
  contact_id: string;
  phone: string;
  centre_contact_id?: number | null;
  name: string;
  username?: string | null;
  avatar_url?: string | null;
  last_body: string;
  last_type: string | null;
  last_direction: 'in' | 'out' | null;
  last_at: string;
  unread: number;
  client_id: number | null;
  order_id: number | null;
  needs_confirm: boolean;
  session: CentreSession;
  owner_user_id?: number | null;
  owner_pseudo?: string | null;
  thread_state?: string | null;
}

export interface CentreQueueItem {
  id: number;
  order_number: string;
  status: string;
  total_amount: number | string | null;
  assigned_to: number | null;
  assigned_to_pseudo?: string | null;
  client_id: number | null;
  created_at: string;
  client_name: string;
  phone: string | null;
  claimed_by: number | null;
  auto_assigned?: boolean;
  unread: number;
  session: CentreSession;
}

export interface CentreLinkedOrder {
  id: number;
  order_number: string;
  status: string;
  total_amount: number | string | null;
  client_id: number | null;
  created_at: string;
  client_name: string | null;
}

export interface CentreMessage {
  id: number;
  direction: 'in' | 'out';
  phone: string;
  channel?: 'whatsapp' | 'messenger' | 'instagram';
  contact_id?: string;
  body: string | null;
  status: string;
  message_type: string;
  template_name: string | null;
  created_at: string;
  wa_message_id: string | null;
  error_message: string | null;
  order_id?: number | null;
  payload?: Record<string, any> | null;
  local_preview?: string | null;
  sent_at?: string | null;
  delivered_at?: string | null;
  read_at_out?: string | null;
  deleted_at?: string | null;
  reactions?: Array<{
    emoji: string;
    from?: 'agent' | 'contact';
    actor_id?: string;
    at?: string;
  }>;
}

export interface CentreTemplate {
  id: number;
  name: string;
  meta_status: string;
  body_wa?: string;
  header_format?: string;
}

export interface CentreTemplatePreview {
  template_id: number;
  template_name: string;
  order_id: number;
  order_number: string | null;
  body: string;
  header_format?: string;
  header_text?: string | null;
  footer_text?: string | null;
  buttons?: Array<{ type: string; text: string; url?: string; phone_number?: string }>;
  has_header_media?: boolean;
}

let tempMessageSeq = 0;

export const createTempMessageId = (): number => {
  tempMessageSeq += 1;
  return -(Date.now() * 1000 + (tempMessageSeq % 1000));
};

export const isTempMessageId = (id: number | string | null | undefined): boolean => Number(id) < 0;

const mergeCallPayload = (prev: CentreMessage, incoming: CentreMessage): CentreMessage => {
  const prevCall = prev.payload?.call;
  const nextCall = incoming.payload?.call;
  if (!prevCall?.has_recording || !nextCall || nextCall.has_recording) return incoming;
  return {
    ...incoming,
    payload: {
      ...incoming.payload,
      call: {
        ...nextCall,
        has_recording: true,
        recording_duration_sec: nextCall.recording_duration_sec ?? prevCall.recording_duration_sec
      }
    }
  };
};

export const upsertCentreMessage = (
  messages: CentreMessage[],
  incoming: CentreMessage,
  replaceTempId?: number | null
): CentreMessage[] => {
  const incomingId = Number(incoming.id);
  let next = [...messages];

  if (replaceTempId != null) {
    const tempIdx = next.findIndex((m) => Number(m.id) === Number(replaceTempId));
    if (tempIdx >= 0) {
      const localPreview = next[tempIdx].local_preview;
      next[tempIdx] = {
        ...incoming,
        local_preview: incoming.local_preview !== undefined ? incoming.local_preview : (localPreview ?? null)
      };
      // Évite le doublon si le socket a déjà ajouté le vrai id
      next = next.filter((m, i) => i === tempIdx || Number(m.id) !== incomingId);
      return next;
    }
  }

  if (incomingId) {
    const idx = next.findIndex((m) => Number(m.id) === incomingId);
    if (idx >= 0) {
      const merged = mergeCallPayload(next[idx], incoming);
      next[idx] = { ...next[idx], ...merged };
      return next;
    }
  }

  // Remplace un pending sortant similaire (socket avant la réponse HTTP)
  if (incoming.direction === 'out' && incoming.body) {
    const pendingIdx = next.findIndex((m) => (
      isTempMessageId(m.id)
      && m.direction === 'out'
      && m.status === 'pending'
      && m.body === incoming.body
      && sameInboxContact(m, incoming)
    ));
    if (pendingIdx >= 0) {
      next[pendingIdx] = incoming;
      return next;
    }
  }

  return [...next, incoming];
};

const sameInboxContact = (a: CentreMessage, b: CentreMessage) => {
  const chA = a.channel || 'whatsapp';
  const chB = b.channel || 'whatsapp';
  if (chA !== chB) return false;
  const idA = a.contact_id || a.phone;
  const idB = b.contact_id || b.phone;
  if (chA === 'whatsapp') return phonesLooseMatch(idA, idB);
  return String(idA) === String(idB);
};

const phonesLooseMatch = (a: string, b: string) => {
  const da = String(a || '').replace(/\D/g, '');
  const db = String(b || '').replace(/\D/g, '');
  if (!da || !db) return String(a || '') === String(b || '');
  return da === db || da.endsWith(db) || db.endsWith(da);
};

const PREVIEW_TTL_MS = 90_000;
const previewCache = new Map<string, { at: number; data: CentreTemplatePreview }>();
const previewInflight = new Map<string, Promise<CentreTemplatePreview>>();
const headerUrlCache = new Map<string, string>();
const headerInflight = new Map<string, Promise<string | null>>();
const ASSIGNEE_TTL_MS = 45_000;
const assigneeCache = new Map<string, { at: number; items: CentreAssignee[] }>();
const assigneeInflight = new Map<string, Promise<CentreAssignee[]>>();
const assigneeKey = (storeId: number, channel: string) => `${storeId}:${channel || 'whatsapp'}`;

const previewCacheKey = (storeId: number, templateId: number, orderId: number) => (
  `${storeId}:${templateId}:${orderId}`
);

export const peekTemplatePreview = (
  storeId: number,
  templateId: number,
  orderId: number
): CentreTemplatePreview | null => {
  const cached = previewCache.get(previewCacheKey(storeId, templateId, orderId));
  if (!cached || Date.now() - cached.at > PREVIEW_TTL_MS) return null;
  return cached.data;
};

const loadPreview = async (
  storeId: number,
  templateId: number,
  orderId: number
): Promise<CentreTemplatePreview> => {
  const key = previewCacheKey(storeId, templateId, orderId);
  const cached = peekTemplatePreview(storeId, templateId, orderId);
  if (cached) return cached;
  const pending = previewInflight.get(key);
  if (pending) return pending;
  const request = apiClient.get<{ success: boolean; data: CentreTemplatePreview }>(
    `/stores/${storeId}/centre/template-preview`,
    { params: { template_id: templateId, order_id: orderId } }
  ).then((response) => {
    const data = response.data.data;
    previewCache.set(key, { at: Date.now(), data });
    return data;
  }).finally(() => {
    previewInflight.delete(key);
  });
  previewInflight.set(key, request);
  return request;
};

const loadHeaderUrl = async (storeId: number, templateId: number): Promise<string | null> => {
  const key = `${storeId}:${templateId}`;
  const cached = headerUrlCache.get(key);
  if (cached) return cached;
  const pending = headerInflight.get(key);
  if (pending) return pending;
  const request = apiClient.get(
    `/stores/${storeId}/centre/templates/${templateId}/header-media`,
    { responseType: 'blob', timeout: 30000 }
  ).then((response) => {
    const url = URL.createObjectURL(response.data as Blob);
    headerUrlCache.set(key, url);
    return url;
  }).catch(() => null).finally(() => {
    headerInflight.delete(key);
  });
  headerInflight.set(key, request);
  return request;
};

class CentreService {
  async getQueue(storeId: number, filter = 'all') {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        items: CentreQueueItem[];
        total: number;
        unread_count: number;
        settings: CentreSettings;
      };
    }>(`/stores/${storeId}/centre/queue`, { params: { filter } });
    return response.data.data;
  }

  async getConversations(storeId: number, filter = 'all', q = '', assign = 'all', assigneeId?: number | null) {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        items: CentreConversation[];
        total: number;
        unread_count: number;
        free_count?: number;
        thread_scope?: 'all' | 'own' | 'none';
        unread_by_channel?: { whatsapp: number; messenger: number; instagram: number };
        allowed_channels?: Array<'whatsapp' | 'messenger' | 'instagram'>;
        settings: CentreSettings;
      };
    }>(`/stores/${storeId}/centre/conversations`, {
      params: { filter, q, assign, assignee_id: assigneeId || undefined }
    });
    return response.data.data;
  }

  peekAssignees(storeId: number, channel = 'whatsapp') {
    const hit = assigneeCache.get(assigneeKey(storeId, channel));
    if (!hit || Date.now() - hit.at > ASSIGNEE_TTL_MS) return null;
    return hit.items;
  }

  prefetchAssignees(storeId: number, channels: string[]) {
    const list = channels.length ? channels : ['whatsapp'];
    list.forEach((channel) => {
      void this.listAssignees(storeId, channel);
    });
  }

  async listAssignees(storeId: number, channel = 'whatsapp', opts?: { fresh?: boolean }) {
    const key = assigneeKey(storeId, channel);
    if (!opts?.fresh) {
      const cached = this.peekAssignees(storeId, channel);
      if (cached) return cached;
    }
    const pending = assigneeInflight.get(key);
    if (pending) return pending;
    const request = apiClient.get<{ success: boolean; data: { items: CentreAssignee[] } }>(
      `/stores/${storeId}/centre/assignees`,
      { params: { channel } }
    ).then((response) => {
      const items = response.data.data.items || [];
      assigneeCache.set(key, { at: Date.now(), items });
      return items;
    }).finally(() => {
      assigneeInflight.delete(key);
    });
    assigneeInflight.set(key, request);
    return request;
  }

  async setPresence(storeId: number, status?: string) {
    const response = await apiClient.post(`/stores/${storeId}/centre/presence`, status ? { status } : {});
    return response.data.data;
  }

  async assignThread(storeId: number, payload: {
    centre_contact_id?: number | null;
    channel?: string;
    contact_id?: string;
    user_id?: number | null;
  }) {
    const response = await apiClient.post(`/stores/${storeId}/centre/assign`, payload);
    return response.data.data;
  }

  async releaseThread(storeId: number, payload: {
    centre_contact_id?: number | null;
    channel?: string;
    contact_id?: string;
  }) {
    const response = await apiClient.post(`/stores/${storeId}/centre/assign/release`, payload);
    return response.data.data;
  }

  async previewAssign(storeId: number, payload: {
    contactIds: number[];
    strategy: string;
    userId?: number | null;
  }) {
    const response = await apiClient.post<{ success: boolean; data: {
      items: { centre_contact_id: number; user_id: number | null }[];
      summary: { user_id: number; pseudo: string; count: number }[];
      skipped: number;
    } }>(`/stores/${storeId}/centre/assign/preview`, payload);
    return response.data.data;
  }

  async applyAssign(storeId: number, items: { centre_contact_id: number; user_id: number | null }[]) {
    const response = await apiClient.post<{ success: boolean; data: { assigned: number; skipped: number } }>(
      `/stores/${storeId}/centre/assign/apply`,
      { items }
    );
    return response.data.data;
  }

  async syncInbox(storeId: number, since: string) {
    const response = await apiClient.get<{
      success: boolean;
      data: { items: CentreMessage[] };
    }>(`/stores/${storeId}/centre/sync`, { params: { since } });
    return response.data.data;
  }

  async getConversation(
    storeId: number,
    contactId: string,
    { includeMessages = true, channel = 'whatsapp' }: { includeMessages?: boolean; channel?: string } = {}
  ) {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        channel: 'whatsapp' | 'messenger' | 'instagram';
        contact_id: string;
        phone: string;
        centre_contact_id: number | null;
        display_name: string | null;
        username: string | null;
        avatar_url: string | null;
        session: CentreSession;
        messages: CentreMessage[];
        orders: CentreLinkedOrder[];
        order: any | null;
        client: any | null;
        client_id: number | null;
        client_orders?: CentreLinkedOrder[];
      };
    }>(`/stores/${storeId}/centre/conversation`, {
      params: {
        channel,
        contact: contactId,
        phone: contactId,
        include_messages: includeMessages ? undefined : 'false'
      }
    });
    return response.data.data;
  }

  async openClient(storeId: number, clientId: number) {
    const response = await apiClient.post(`/stores/${storeId}/centre/open-client`, { client_id: clientId });
    return response.data.data;
  }

  async getUnread(storeId: number) {
    const response = await apiClient.get<{
      success: boolean;
      data: { unread_count: number };
    }>(`/stores/${storeId}/centre/unread`);
    return response.data.data.unread_count;
  }

  async linkContact(storeId: number, contactId: number, clientId: number) {
    const response = await apiClient.post(`/stores/${storeId}/centre/contacts/${contactId}/link`, {
      client_id: clientId
    });
    return response.data.data;
  }

  async getSettings(storeId: number) {
    const response = await apiClient.get<{
      success: boolean;
      data: { settings: CentreSettings };
    }>(`/stores/${storeId}/centre/settings`);
    return response.data.data.settings;
  }

  async updateSettings(storeId: number, payload: Partial<CentreSettings>) {
    const response = await apiClient.put<{
      success: boolean;
      data: { settings: CentreSettings };
    }>(`/stores/${storeId}/centre/settings`, payload);
    return response.data.data.settings;
  }

  async claim(storeId: number, orderId: number) {
    const response = await apiClient.post(`/stores/${storeId}/centre/claim`, { order_id: orderId });
    return response.data.data;
  }

  async next(storeId: number) {
    const response = await apiClient.post(`/stores/${storeId}/centre/next`);
    return response.data.data;
  }

  async release(storeId: number, orderId: number) {
    const response = await apiClient.post(`/stores/${storeId}/centre/release`, { order_id: orderId });
    return response.data.data as { released: boolean };
  }

  async assign(storeId: number, orderId: number, assignedTo: number | null) {
    const response = await apiClient.post(`/stores/${storeId}/centre/assign`, {
      order_id: orderId,
      assigned_to: assignedTo
    });
    return response.data.data as {
      order: any;
      claim: { user_id: number; order_id: number; auto_assigned?: boolean } | null;
    };
  }

  async heartbeat(storeId: number, orderId: number) {
    await apiClient.post(`/stores/${storeId}/centre/heartbeat`, { order_id: orderId });
  }

  async deleteConversation(
    storeId: number,
    contactId: string,
    channel: string = 'whatsapp'
  ) {
    await apiClient.delete(`/stores/${storeId}/centre/conversation`, {
      params: { channel, contact: contactId, phone: contactId }
    });
  }

  async getThread(
    storeId: number,
    contactId: string,
    {
      limit = 40,
      beforeId,
      markRead = true,
      channel = 'whatsapp'
    }: { limit?: number; beforeId?: number; markRead?: boolean; channel?: string } = {}
  ) {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        messages: CentreMessage[];
        session: CentreSession;
        phone: string;
        channel?: string;
        contact_id?: string;
        has_more?: boolean;
      };
    }>(`/stores/${storeId}/centre/thread`, {
      params: {
        channel,
        contact: contactId,
        phone: contactId,
        limit,
        before_id: beforeId || undefined,
        mark_read: markRead ? undefined : 'false'
      }
    });
    return response.data.data;
  }

  async reply(
    storeId: number,
    payload: {
      phone?: string;
      channel?: string;
      contact_id?: string;
      order_id?: number;
      text?: string;
      message_template_id?: number;
      file?: File;
      voice?: boolean;
      reply_to?: number | null;
    }
  ) {
    if (payload.file) {
      const formData = new FormData();
      if (payload.order_id) formData.append('order_id', String(payload.order_id));
      if (payload.phone) formData.append('phone', payload.phone);
      if (payload.channel) formData.append('channel', payload.channel);
      if (payload.contact_id) formData.append('contact_id', payload.contact_id);
      if (payload.text) formData.append('text', payload.text);
      if (payload.voice) formData.append('voice', '1');
      if (payload.reply_to) formData.append('reply_to', String(payload.reply_to));
      formData.append('file', payload.file);
      const response = await apiClient.post(`/stores/${storeId}/centre/reply`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      return response.data.data;
    }
    const response = await apiClient.post(`/stores/${storeId}/centre/reply`, payload);
    return response.data.data;
  }

  async revokeMessage(storeId: number, messageId: number) {
    const response = await apiClient.post<{
      success: boolean;
      data: { message: CentreMessage };
    }>(`/stores/${storeId}/centre/messages/${messageId}/revoke`);
    return response.data.data.message;
  }

  async reactMessage(storeId: number, messageId: number, emoji: string) {
    const response = await apiClient.post<{
      success: boolean;
      data: { message: CentreMessage };
    }>(`/stores/${storeId}/centre/messages/${messageId}/react`, { emoji });
    return response.data.data.message;
  }

  async getMediaBlob(storeId: number, messageId: number) {
    const response = await apiClient.get(`/stores/${storeId}/centre/messages/${messageId}/media`, {
      responseType: 'blob',
      timeout: 30000
    });
    return response.data as Blob;
  }

  async outcome(storeId: number, orderId: number, outcomeKey: string) {
    const response = await apiClient.post(`/stores/${storeId}/centre/outcome`, {
      order_id: orderId,
      outcome_key: outcomeKey
    });
    return response.data.data;
  }

  async listTemplates(storeId: number) {
    const response = await apiClient.get<{
      success: boolean;
      data: { templates: CentreTemplate[] };
    }>(`/stores/${storeId}/centre/templates`);
    return response.data.data.templates;
  }

  async previewTemplate(storeId: number, templateId: number, orderId: number) {
    return loadPreview(storeId, templateId, orderId);
  }

  prefetchTemplatePreview(storeId: number, templateId: number, orderId: number) {
    void loadPreview(storeId, templateId, orderId).then((preview) => {
      if (preview.has_header_media) void loadHeaderUrl(storeId, templateId);
    });
  }

  async getTemplateHeaderBlob(storeId: number, templateId: number) {
    const url = await loadHeaderUrl(storeId, templateId);
    if (!url) throw new Error('MEDIA_NOT_FOUND');
    const response = await fetch(url);
    return response.blob();
  }

  async getTemplateHeaderUrl(storeId: number, templateId: number) {
    return loadHeaderUrl(storeId, templateId);
  }

  async getCall(storeId: number, callId: number) {
    const response = await apiClient.get<{ success: boolean; data: { call: CentreCall } }>(
      `/stores/${storeId}/centre/calls/${callId}`
    );
    return response.data.data.call;
  }

  async startCall(storeId: number, payload: {
    phone: string;
    sdp: string;
    sdp_type?: string;
    contact_name?: string;
    order_id?: number | null;
  }) {
    const response = await apiClient.post<{
      success: boolean;
      data: { need_permission?: boolean; call?: CentreCall };
    }>(`/stores/${storeId}/centre/calls`, payload);
    return response.data.data;
  }

  async claimCall(storeId: number, callId: number) {
    const response = await apiClient.post<{
      success: boolean;
      data: { call: CentreCall; claimed?: boolean };
    }>(`/stores/${storeId}/centre/calls/${callId}/claim`);
    return response.data.data;
  }

  async acceptCall(storeId: number, callId: number, payload: { sdp: string; sdp_type?: string }) {
    const response = await apiClient.post<{ success: boolean; data: { call: CentreCall } }>(
      `/stores/${storeId}/centre/calls/${callId}/accept`,
      payload
    );
    return response.data.data;
  }

  async rejectCall(storeId: number, callId: number) {
    await apiClient.post(`/stores/${storeId}/centre/calls/${callId}/reject`);
  }

  async hangupCall(storeId: number, callId: number) {
    await apiClient.post(`/stores/${storeId}/centre/calls/${callId}/hangup`);
  }

  async getCallRecordingStatus(storeId: number) {
    const response = await apiClient.get<{
      success: boolean;
      data: { enabled: boolean };
    }>(`/stores/${storeId}/centre/recording-status`);
    return Boolean(response.data.data.enabled);
  }

  async uploadCallRecording(storeId: number, callId: number, blob: Blob, durationSec?: number | null) {
    const formData = new FormData();
    const ext = blob.type.includes('mp4') ? 'm4a' : 'webm';
    formData.append('file', blob, `appel-${callId}.${ext}`);
    if (durationSec != null) formData.append('duration_sec', String(Math.round(durationSec)));
    await apiClient.post(`/stores/${storeId}/centre/calls/${callId}/recording`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000
    });
  }

  async getCallRecordingBlob(storeId: number, callId: number) {
    const response = await apiClient.get(`/stores/${storeId}/centre/calls/${callId}/recording`, {
      responseType: 'blob',
      timeout: 30000
    });
    return response.data as Blob;
  }
}

export interface CentreCall {
  id: number;
  wa_call_id?: string | null;
  provider?: 'whatsapp' | 'voip';
  phone: string;
  direction: 'in' | 'out';
  status: string;
  order_id: number | null;
  client_id: number | null;
  assigned_user_id: number | null;
  answered_by: number | null;
  contact_name: string | null;
  sdp?: { remote?: string; remote_type?: string };
  started_at?: string | null;
  ended_at?: string | null;
  duration_sec?: number | null;
}

export default new CentreService();
